import { X, DownloadSimple, Camera, MapPin, GraduationCap, TrendUp, House, Buildings, CurrencyDollar, PencilSimple } from "@phosphor-icons/react";

const money = (n) => (n || n === 0) ? `$${Number(n).toLocaleString()}` : "—";

const Stat = ({ label, value, sub, primary }) => (
  <div className="rounded-[5px] border border-black/10 bg-white/60 p-4">
    <p className="text-[10px] uppercase tracking-[0.15em] text-black/45 font-semibold whitespace-nowrap truncate">{label}</p>
    <p className="font-display text-2xl mt-1 whitespace-nowrap truncate" style={{ color: primary || "#4A0000" }}>{value}</p>
    {sub && <p className="text-xs text-black/50 mt-0.5 truncate">{sub}</p>}
  </div>
);

const SlideHead = ({ n, kicker, title, accent, dark }) => (
  <div className="mb-6">
    <div className="flex items-center gap-2 mb-1">
      <span className="text-[11px] font-black tracking-[0.25em] uppercase whitespace-nowrap" style={{ color: accent }}>{kicker}</span>
      {n && <span className={`text-[11px] font-mono ${dark ? "text-white/40" : "text-black/30"}`}>{String(n).padStart(2, "0")}</span>}
    </div>
    <h2 className="font-display text-3xl leading-tight" style={{ color: dark ? "#FBFAF7" : "#1A1715" }}>{title}</h2>
  </div>
);

export default function LpgDeck({ data, branding, onClose, onEdit, toolbarExtra }) {
  const d = data || {};
  const b = branding || {};
  const primary = b.primary || "#4A0000";
  const accent = b.accent || "#B08D57";
  const p = d.property || {};
  const es = d.executive_summary || {};
  const mk = d.market || {};
  const rec = d.recommendation || {};
  const nb = d.neighborhood || {};
  const sch = d.schools || {};
  const lf = d.lifestyle || {};
  const inv = d.investment || {};

  // Strategic pricing range: low (advantageous entry) -> high (top of market), likely lands between.
  const PriceRange = ({ lo, hi, likely, light }) => {
    // When the AI didn't supply an explicit low/high, derive a $50k band around the
    // likely price: $20k on the low side, $30k on the high side.
    const base = likely || lo || hi;
    const dLo = (lo && hi) ? lo : (base ? base - 20000 : null);
    const dHi = (lo && hi) ? hi : (base ? base + 30000 : null);
    const mid = likely || base;
    return (
      <div>
        {(dLo && dHi) ? (
          <div className="flex items-end gap-2 flex-nowrap whitespace-nowrap">
            <span className="font-display text-4xl sm:text-5xl leading-none">{money(dLo)}</span>
            <span className="font-display text-2xl leading-none pb-1" style={{ opacity: 0.45 }}>–</span>
            <span className="font-display text-4xl sm:text-5xl leading-none">{money(dHi)}</span>
          </div>
        ) : (
          <span className="font-display text-5xl leading-none whitespace-nowrap">{money(base)}</span>
        )}
        {mid && dLo && dHi && (
          <p className={`text-sm mt-2 whitespace-nowrap ${light ? "text-white/70" : "text-black/60"}`}>
            Most likely lands near <span className="font-semibold" style={{ color: accent }}>{money(mid)}</span>
          </p>
        )}
      </div>
    );
  };

  // Deck theme surfaces derive from the brokerage brand: dark surface = brand primary
  // (minimal = a brand-tinted near-black), accents come from the brand accent.
  const TH = ({ editorial: { dark: primary, light: "#FBFAF7" },
    minimal: { dark: `color-mix(in srgb, ${primary} 20%, #14110F)`, light: "#FFFFFF" },
    bold: { dark: primary, light: `${primary}0D` } }[b.theme || "editorial"]) || { dark: primary, light: "#FBFAF7" };

  const Slide = ({ children, dark, className = "" }) => (
    <section className={`lpg-slide relative rounded-[6px] overflow-hidden p-8 sm:p-10 ${className}`}
      style={dark ? { background: TH.dark, color: "#fff", breakInside: "avoid", pageBreakInside: "avoid" } : { background: TH.light, color: "#1A1715", border: "1px solid rgba(0,0,0,0.08)", breakInside: "avoid", pageBreakInside: "avoid" }}>
      {children}
      <div className="flex items-center justify-between mt-8 pt-4 text-[10px] uppercase tracking-widest" style={{ borderTop: `1px solid ${dark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.08)"}`, opacity: 0.6 }}>
        <span>{b.brokerage || "Reelty"}</span>
        <span>{d.address || ""}</span>
      </div>
    </section>
  );

  return (
    <div data-testid="lpg-deck-overlay" className="lpg-deck-overlay fixed inset-0 z-50 bg-[#EDE8DD] overflow-y-auto">
      <div className="lpg-toolbar sticky top-0 z-10 flex items-center justify-between px-4 sm:px-8 py-3 bg-white/85 backdrop-blur-md border-b border-black/10">
        <div className="flex items-center gap-2 min-w-0">
          {b.logo ? <img src={b.logo} alt="" className="h-6 w-auto object-contain" /> : <span className="font-display text-lg" style={{ color: primary }}>Reelty</span>}
          <span className="text-sm text-black/50 truncate hidden sm:block">· {d.address}</span>
        </div>
        <div className="flex items-center gap-2">
          {toolbarExtra}
          {onEdit && <button data-testid="lpg-edit-btn" onClick={onEdit} className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-[5px] border border-black/15 hover:bg-black/5"><PencilSimple size={15} />Edit</button>}
          <button data-testid="lpg-download-pdf" onClick={() => window.print()} className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-[5px] text-white" style={{ background: primary }}><DownloadSimple size={15} weight="bold" />Download PDF</button>
          <button data-testid="lpg-close-deck" onClick={onClose} className="p-1.5 rounded-full hover:bg-black/10"><X size={20} /></button>
        </div>
      </div>

      <div className="lpg-deck max-w-4xl mx-auto p-4 sm:p-8 space-y-6">
        {/* Cover */}
        <Slide dark>
          <p className="text-[11px] font-black tracking-[0.3em] uppercase" style={{ color: accent }}>Real Estate Media & Market Strategy</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-4 leading-none">{p.headline || d.address}</h1>
          <p className="mt-3 text-white/70">{d.address}{p.neighborhood ? ` · ${p.neighborhood}` : ""}{p.submarket ? ` · ${p.submarket}` : ""}</p>
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-6 text-sm text-white/85">
            {p.beds != null && <span>{p.beds} Bed</span>}
            {p.baths != null && <span>{p.baths} Bath</span>}
            {p.sqft != null && <span>{Number(p.sqft).toLocaleString()} Sqft</span>}
            {p.year_built != null && <span>Built {p.year_built}</span>}
            {p.garage && <span>{p.garage}</span>}
          </div>
          <div className="flex items-center gap-3 mt-8">
            {b.avatar && <img src={b.avatar} alt="" className="w-11 h-11 rounded-full object-cover ring-2" style={{ ["--tw-ring-color"]: accent }} />}
            <div>
              <p className="font-semibold">{b.agent_name}</p>
              <p className="text-xs text-white/60">{[b.title, b.brokerage].filter(Boolean).join(" · ")}</p>
              <p className="text-xs text-white/60">{[b.email, b.phone].filter(Boolean).join(" · ")}</p>
            </div>
          </div>
        </Slide>

        {/* Property */}
        <Slide>
          <SlideHead n={2} kicker="The Property" title={p.headline || "Property overview"} accent={accent} />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Square feet" value={p.sqft != null ? Number(p.sqft).toLocaleString() : "—"} primary={primary} />
            <Stat label="Bed / Bath" value={`${p.beds ?? "—"} / ${p.baths ?? "—"}`} primary={primary} />
            <Stat label="Lot" value={p.lot_acres != null ? `${p.lot_acres} ac` : "—"} primary={primary} />
            <Stat label="Year built" value={p.year_built ?? "—"} primary={primary} />
            <Stat label="Garage" value={p.garage || "—"} primary={primary} />
            <Stat label="HOA" value={p.hoa || "—"} primary={primary} />
            <Stat label="Subdivision" value={p.subdivision || "—"} primary={primary} />
            <Stat label="Submarket" value={p.submarket || "—"} primary={primary} />
          </div>
        </Slide>

        {/* Executive Summary */}
        <Slide dark>
          <SlideHead n={3} kicker="Executive Summary" title={es.headline || "The recommendation"} accent={accent} dark />
          <div className="rounded-[6px] p-5 mb-5" style={{ background: "rgba(255,255,255,0.06)", border: `1px solid ${accent}55` }}>
            <p className="text-[11px] uppercase tracking-widest" style={{ color: accent }}>Recommended list price range</p>
            <div className="mt-1"><PriceRange lo={rec.price_low ?? es.price_low} hi={rec.price_high ?? es.price_high} likely={rec.likely_price ?? es.recommended_price ?? rec.price} light /></div>
            <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-sm text-white/75">
              <span>~${es.price_per_sqft}/sqft</span>
              <span>{es.days_to_offer} to offer</span>
              <span>{es.list_to_sale_ratio} list-to-sale</span>
              <span>{es.market_temp}</span>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {(es.bullets || []).map((x, i) => (
              <div key={i} className="rounded-[5px] p-4" style={{ background: "rgba(255,255,255,0.05)" }}>
                <p className="font-semibold" style={{ color: accent }}>{x.title}</p>
                <p className="text-sm text-white/75 mt-1">{x.text}</p>
              </div>
            ))}
          </div>
        </Slide>

        {/* Market at a glance */}
        <Slide>
          <SlideHead n={4} kicker="Market at a Glance" title={mk.window ? `${mk.window}` : "Local market snapshot"} accent={accent} />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Median sold" value={mk.median_sold_price || "—"} sub={mk.median_yoy} primary={primary} />
            <Stat label="Median $/sqft" value={mk.median_ppsf || "—"} sub={mk.ppsf_yoy} primary={primary} />
            <Stat label="Median DOM" value={mk.median_dom || "—"} primary={primary} />
            <Stat label="List-to-sale" value={mk.list_to_sale || "—"} primary={primary} />
            <Stat label="Active listings" value={mk.active_listings || "—"} sub={mk.months_supply} primary={primary} />
            <Stat label="Cash buyers" value={mk.cash_buyers || "—"} primary={primary} />
            <Stat label="3-yr appreciation" value={mk.appreciation_3yr || "—"} primary={primary} />
            <Stat label="Temperature" value={mk.temperature || "—"} primary={primary} />
          </div>
          {(d.price_trend?.points || []).length > 0 && (
            <div className="mt-6">
              <p className="text-[11px] uppercase tracking-widest text-black/45 mb-3 font-semibold">{d.price_trend?.title || "Price trend"}</p>
              <div className="flex items-end gap-3 h-32">
                {d.price_trend.points.map((pt, i) => {
                  const vals = d.price_trend.points.map((x) => Number(x.price) || 0);
                  const max = Math.max(...vals, 1);
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
                      <span className="text-[10px] text-black/50 mb-1">{money(pt.price)}</span>
                      <div className="w-full rounded-t-[4px]" style={{ height: `${(Number(pt.price) / max) * 100}%`, background: i === d.price_trend.points.length - 1 ? accent : primary }} />
                      <span className="text-[10px] text-black/50 mt-1">{pt.year}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Slide>

        {/* Comps */}
        {(d.comps || []).length > 0 && (
          <Slide>
            <SlideHead n={5} kicker="Recent Comparable Sales" title="The sales that set the price" accent={accent} />
            <div className="grid sm:grid-cols-2 gap-3">
              {d.comps.map((c, i) => (
                <div key={i} className="rounded-[5px] border border-black/10 bg-white p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-wider text-black/45">{c.distance_note}</span>
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-[4px]" style={{ background: `${accent}22`, color: primary }}>{c.status}</span>
                  </div>
                  <p className="font-semibold mt-1 truncate whitespace-nowrap">{c.address}</p>
                  <p className="text-xs text-black/50">{c.beds} bd · {c.baths} ba · {c.sqft ? Number(c.sqft).toLocaleString() : "—"} sf</p>
                  <p className="font-display text-2xl mt-1" style={{ color: primary }}>{money(c.price)}</p>
                  <p className="text-xs text-black/50">{[c.dom, c.sold_date].filter(Boolean).join(" · ")}</p>
                  {c.narrative && <p className="text-xs text-black/60 mt-2">{c.narrative}</p>}
                </div>
              ))}
            </div>
          </Slide>
        )}

        {/* Pricing logic */}
        {d.pricing_logic && (
          <Slide dark>
            <SlideHead n={6} kicker="The Pricing Logic" title={d.pricing_logic.title || "Why this number"} accent={accent} dark />
            <p className="font-display text-4xl mb-4">{money(d.pricing_logic.recommended_price || rec.price)}</p>
            <div className="space-y-3">
              {(d.pricing_logic.reasons || []).map((r, i) => (
                <div key={i} className="rounded-[5px] p-4" style={{ background: "rgba(255,255,255,0.05)" }}>
                  <p className="font-semibold" style={{ color: accent }}>{r.title}</p>
                  <p className="text-sm text-white/75 mt-1">{r.text}</p>
                </div>
              ))}
            </div>
          </Slide>
        )}

        {/* Active competition */}
        {(d.active_competition || []).length > 0 && (
          <Slide>
            <SlideHead n={7} kicker="Active Competition" title="Who buyers cross-shop" accent={accent} />
            <div className="grid sm:grid-cols-3 gap-3">
              {d.active_competition.map((c, i) => (
                <div key={i} className="rounded-[5px] border border-black/10 bg-white p-4">
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-[4px]" style={{ background: `${accent}22`, color: primary }}>{c.status}</span>
                  <p className="font-semibold mt-1 truncate whitespace-nowrap">{c.address}</p>
                  <p className="text-xs text-black/50">{c.beds} bd · {c.baths} ba · {c.sqft ? Number(c.sqft).toLocaleString() : "—"} sf</p>
                  <p className="font-display text-xl mt-1" style={{ color: primary }}>{money(c.price)}</p>
                  <p className="text-xs text-black/50">{c.dom_note}</p>
                  {c.narrative && <p className="text-xs text-black/60 mt-2">{c.narrative}</p>}
                </div>
              ))}
            </div>
          </Slide>
        )}

        {/* Recommendation */}
        <Slide dark>
          <SlideHead n={8} kicker="Our Recommendation" title={rec.summary ? "List with confidence" : "The plan"} accent={accent} dark />
          <p className="text-[11px] uppercase tracking-widest mb-1" style={{ color: accent }}>Strategic list price range</p>
          <PriceRange lo={rec.price_low ?? es.price_low} hi={rec.price_high ?? es.price_high} likely={rec.likely_price ?? rec.price ?? es.recommended_price} light />
          <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-white/75">
            {rec.price_per_sqft && <span>~${rec.price_per_sqft}/sqft</span>}
            {rec.days_to_offer && <span>{rec.days_to_offer}</span>}
          </div>
          {rec.summary && <p className="text-white/80 mt-4 max-w-2xl">{rec.summary}</p>}
          {(rec.how_we_hit || []).length > 0 && (
            <div className="mt-5">
              <p className="text-[11px] uppercase tracking-widest mb-2" style={{ color: accent }}>How we get there</p>
              <ul className="space-y-1.5">
                {rec.how_we_hit.map((h, i) => <li key={i} className="text-sm text-white/80 flex gap-2"><span style={{ color: accent }}>—</span>{h}</li>)}
              </ul>
            </div>
          )}
        </Slide>

        {/* Neighborhood */}
        {nb.name && (
          <Slide>
            <SlideHead n={9} kicker="The Neighborhood" title={nb.name} accent={accent} />
            {nb.summary && <p className="text-black/70 mb-5 max-w-2xl">{nb.summary}</p>}
            <div className="grid sm:grid-cols-3 gap-3">
              {(nb.amenities || []).map((a, i) => (
                <div key={i} className="rounded-[5px] border border-black/10 bg-white p-4">
                  <MapPin size={18} weight="duotone" style={{ color: accent }} />
                  <p className="font-semibold mt-2">{a.name}</p>
                  <p className="text-xs text-black/50">{a.desc}{a.distance ? ` · ${a.distance}` : ""}</p>
                </div>
              ))}
            </div>
            {nb.tailwinds && <div className="mt-5 rounded-[5px] p-4" style={{ background: `${accent}18` }}><p className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: primary }}>Tailwinds</p><p className="text-sm text-black/70 mt-1">{nb.tailwinds}</p></div>}
          </Slide>
        )}

        {/* Schools */}
        {(sch.list || []).length > 0 && (
          <Slide>
            <SlideHead n={10} kicker="Schools" title={sch.district || "Assigned schools"} accent={accent} />
            <div className="grid sm:grid-cols-3 gap-3">
              {sch.list.map((s, i) => (
                <div key={i} className="rounded-[5px] border border-black/10 bg-white p-4">
                  <div className="flex items-center gap-2"><GraduationCap size={18} weight="duotone" style={{ color: accent }} />
                    <span className="text-xs font-bold px-1.5 py-0.5 rounded-full" style={{ background: `${primary}12`, color: primary }}>{s.score}</span>
                  </div>
                  <p className="font-semibold mt-2">{s.name}</p>
                  <p className="text-xs text-black/50">{[s.grades, s.distance, s.rating].filter(Boolean).join(" · ")}</p>
                  {s.desc && <p className="text-xs text-black/60 mt-2">{s.desc}</p>}
                </div>
              ))}
            </div>
            {sch.disclaimer && <p className="text-[11px] text-black/40 mt-3">{sch.disclaimer}</p>}
          </Slide>
        )}

        {/* Lifestyle + Investment */}
        {(lf.walk_score != null || inv.monthly_rent) && (
          <Slide dark>
            <SlideHead n={11} kicker="Lifestyle & Investment" title="Location and returns" accent={accent} dark />
            {lf.walk_score != null && (
              <div className="grid grid-cols-3 gap-3 mb-5">
                <Stat label="Walk score" value={lf.walk_score} primary={accent} />
                <Stat label="Bike score" value={lf.bike_score} primary={accent} />
                <Stat label="Transit score" value={lf.transit_score} primary={accent} />
              </div>
            )}
            {inv.monthly_rent && (
              <div className="rounded-[5px] p-4" style={{ background: "rgba(255,255,255,0.05)" }}>
                <p className="text-[11px] uppercase tracking-widest" style={{ color: accent }}>Investment angle</p>
                <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm text-white/80">
                  <span>Rent {inv.monthly_rent}/mo</span>
                  <span>Yield {inv.gross_yield}</span>
                  <span>Cap {inv.cap_rate}</span>
                  <span>{inv.gross_annual}/yr</span>
                </div>
                {inv.thesis && <p className="text-sm text-white/70 mt-3">{inv.thesis}</p>}
                {inv.disclaimer && <p className="text-[11px] text-white/40 mt-2">{inv.disclaimer}</p>}
              </div>
            )}
          </Slide>
        )}

        {/* CTA */}
        <Slide dark>
          <SlideHead kicker="Let's List It" title="Ready when you are" accent={accent} dark />
          <div className="grid sm:grid-cols-2 gap-3">
            {(d.cta?.steps || []).map((s, i) => (
              <div key={i} className="rounded-[5px] p-4 flex gap-3" style={{ background: "rgba(255,255,255,0.05)" }}>
                <span className="font-display text-2xl" style={{ color: accent }}>{i + 1}</span>
                <div><p className="font-semibold">{s.title}</p><p className="text-sm text-white/70 mt-0.5">{s.text}</p></div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3 mt-8">
            {b.avatar && <img src={b.avatar} alt="" className="w-12 h-12 rounded-full object-cover ring-2" style={{ ["--tw-ring-color"]: accent }} />}
            <div>
              <p className="font-semibold text-lg">{b.agent_name}</p>
              <p className="text-sm text-white/70">{[b.title, b.brokerage].filter(Boolean).join(" · ")}</p>
              <p className="text-sm text-white/70">{[b.email, b.phone, b.website].filter(Boolean).join(" · ")}</p>
            </div>
          </div>
        </Slide>
      </div>
    </div>
  );
}
